import ServicePageHero from "../components/ServicePageHero";
import { openConsultationMail } from "../config/contact";
import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiArrowUpRight, FiPlus, FiChevronDown, FiChevronUp, FiLayout, FiUsers, FiZap, FiSettings } from "react-icons/fi";
import { Helmet } from "react-helmet-async";
import ServiceDetailSection from "../components/ServiceDetailSection";
import ServiceIntroSection from "../components/ServiceIntroSection";

// Using specific assets for Web Design 
import img_digital_tools_bro from "../assets/Digital tools-bro.png";
import img_portfolio_rafiki from "../assets/Portfolio-rafiki.png";
import img_windows_amico from "../assets/Windows-amico.png";
import img_windows_bro from "../assets/Windows-bro.png";
import img_windows_rafiki from "../assets/Windows-rafiki.png";
import img_about_img1 from "../assets/Choose-bro.png";
import img_about_img2 from "../assets/Starting a business project-amico.png";

const FAQ_DATA = [
  {
    question: "1. How long does it take to develop a website?",
    answer: "The timeline depends on the complexity of the website. A basic website can take 1–2 weeks, while more complex projects may take longer."
  },
  {
    question: "2. Will my website be mobile-friendly?",
    answer: "Yes, we create responsive websites that work smoothly across all devices, including mobile and tablets."
  },
  {
    question: "3. Can I update my website content later?",
    answer: "Yes, we build websites that allow easy content updates so you can manage your website without technical knowledge."
  },
  {
    question: "4. Do you provide website maintenance services?",
    answer: "Yes, we offer ongoing support and maintenance to keep your website updated and running smoothly."
  }
];

const scrollReveal = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.6, ease: "easeOut" },
};

const staggerContainer = {
  initial: {},
  whileInView: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemFade = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: "easeOut" },
};


const WHY_ICONS = [
  <FiLayout />,
  <FiUsers />,
  <FiZap />,
  <FiSettings />,
];
const WebDesignPage = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [activeWhy, setActiveWhy] = useState(0);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const scrollToContact = openConsultationMail; // replaced

  return (
    <>
      <Helmet>
        <title>Website Development Company in India | Hire Web Experts</title>
        <meta name="description" content="Build a professional website with expert developers. Hire a website development company in India for responsive and business-focused websites." />
      </Helmet>
      <main className="web-design-page">
      <ServicePageHero
        badge="Our Services"
        title="Best Web Design & Development"
        subtitle="Build a fast, user-friendly website that supports your business growth."
        breadcrumb="Home > Services > Web Design"
        image={img_windows_amico}
      />


      {/* ABOUT SECTION */}
      <ServiceIntroSection
        heading="How a Great Website Drives Business Growth"
        imageLeft={img_windows_bro}
        imageRight={img_portfolio_rafiki}
        paragraph1="A well-designed website is the foundation of your online presence. It helps your business build credibility, attract potential customers, and create a strong first impression."
        paragraph2="As a professional web design company, we focus on creating responsive, user-friendly websites that align with your business goals."
      />

      <ServiceDetailSection
        isReversed={false}
        label="Our Web Design & Development Strategy"
        title="Planning & Structure"
        description="We start by understanding your business, goals, and target audience to plan the website structure effectively. This ensures the website is aligned with your brand and user needs."
        longDescription="A clear structure helps users navigate easily and improves overall experience on your website."
        image={img_windows_bro}
      />

      <ServiceDetailSection
        isReversed={true} bgLight
        title="Design & User Experience"
        description="We create clean and modern designs that reflect your brand identity and provide a smooth user experience. Our focus is on making your website visually appealing and easy to use."
        longDescription="A well-designed website keeps users engaged and encourages them to take action."
        image={img_digital_tools_bro}
      />

      <ServiceDetailSection
        isReversed={false}
        title="Development & Performance"
        description="We build fast, responsive, and mobile-friendly websites that perform well across all devices. Our development process ensures smooth functionality and reliability."
        longDescription="This helps improve user experience and supports better performance in search engines."
        image={img_windows_rafiki}
      />

      <ServiceDetailSection
        isReversed={true} bgLight
        title="Testing & Optimization"
        description="Before launch, we test your website for performance, usability, and responsiveness. We fix issues and optimize the website to ensure everything works perfectly."
        longDescription="This ensures your website is ready to deliver a seamless experience from day one."
        image={img_windows_amico}
      />

      {/* WHY CHOOSE US - DARK VERSION */}
      <section className="dark-why-section">
        <div className="container-custom">
          <motion.div className="dark-why-header" {...scrollReveal}>
            <span className="dark-why-badge">Why Choose Us</span>
            <h2>Why Businesses Trust Our Web Development Expertise</h2>
          </motion.div>

          <motion.div 
            className="dark-why-grid"
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true, amount: 0.1 }}
          >
            <motion.div className="dark-why-list" variants={itemFade}>
              {[
                { 
                  title: "1. Custom Website Solutions", 
                  desc: "We create websites tailored to your business needs instead of using generic templates. This ensures your website stands out and aligns with your brand. Our customized approach helps deliver better user experience and business results." 
                },
                { 
                  title: "2. User-Focused Design", 
                  desc: "We focus on creating websites that are easy to navigate and user-friendly. This helps visitors stay longer and engage with your content. A good user experience increases the chances of conversions and growth." 
                },
                { 
                  title: "3. Responsive & Fast Websites", 
                  desc: "We develop websites that work smoothly across all devices and load quickly. This improves both user experience and search engine performance. A fast and responsive website is essential for modern businesses." 
                },
                { 
                  title: "4. Ongoing Support & Improvements", 
                  desc: "We provide support and improvements to keep your website updated and optimized. This helps your business stay competitive online. Regular updates ensure your website continues to perform well over time." 
                }
              ].map((item, idx) => (
                <div 
                  key={idx} 
                  className={`dark-why-item ${activeWhy === idx ? 'active' : ''}`}
                  onMouseEnter={() => setActiveWhy(idx)}
                >
                  <div className="dark-why-item-header">
                    <span className="dark-why-item-icon-left">{WHY_ICONS[idx]}</span>
                    <h4>{item.title}</h4>
                    <span className="dark-why-item-icon">
                      {activeWhy === idx ? <FiArrowUpRight /> : <FiPlus />}
                    </span>
                  </div>
                  <div className="dark-why-item-content">
                    <p>{item.desc}</p>
                  </div>
                </div>
              ))}
            </motion.div>

            <motion.div className="dark-why-visual" variants={itemFade}>
              <img src={img_about_img1} alt="Expertise 1" className="dark-oval-1" />
              <img src={img_about_img2} alt="Expertise 2" className="dark-oval-2" />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="faq-section py-20">
        <div className="container-custom max-w-4xl">
          <motion.h2 className="section-title text-center mb-16" {...scrollReveal}>Frequently Asked Questions</motion.h2>
          <div className="faq-list">
            {FAQ_DATA.map((faq, index) => (
              <motion.div 
                key={index} 
                className={`faq-item ${openFaq === index ? 'active' : ''}`}
                {...scrollReveal}
                transition={{ delay: index * 0.1 }}
              >
                <button className="faq-question" onClick={() => toggleFaq(index)}>
                  <span>{faq.question}</span>
                  {openFaq === index ? <FiChevronUp /> : <FiChevronDown />}
                </button>
                <AnimatePresence>
                  {openFaq === index && (
                    <motion.div 
                      className="faq-answer"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <p>{faq.answer}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="service-page-cta service-page-cta--soft py-20">
        <div className="container-custom">
          <motion.div 
            className="service-page-cta-inner text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2>Build a Website That Works for Your Business</h2>
            <p className="mb-8">Our website design and development services in Delhi NCR are focused on creating user-friendly, high-performing websites that support your business growth.</p>
            <button className="nav-btn mx-auto" onClick={scrollToContact} style={{ padding: '14px 32px', fontSize: '16px' }}>
              Get Free Consultation
              <span className="nav-btn-arrow" style={{ width: '28px', height: '28px' }}>
                <FiArrowUpRight />
              </span>
            </button>
          </motion.div>
        </div>
      </section>
      </main>
    </>
  );
};

export default WebDesignPage;
